/**
 * VISTOERA_PLATFORM_BRANDING_SETTINGS_DYNAMIC_FAVICON_P0_V1 — Platform Admin
 * → 系统设置 → 品牌设置 (favicon upload).
 */
import { UploadOutlined } from '@ant-design/icons'
import { Button, Image, Spin, Typography, Upload, message } from 'antd'
import type { UploadProps } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { platformGetBrandingSettings, platformUploadBrandingFavicon } from '../api'
import type { PlatformBrandingSettingsOut } from '../types'
import { BUNDLED_FAVICON_32, applyUploadedPlatformFavicon, resolvePlatformBrandingAssetUrl } from '../platformBranding'
import { SectionCard, formatDateTime } from './adminShared'
import { platformColors } from './theme'

export default function SystemSettingsPage() {
  const [settings, setSettings] = useState<PlatformBrandingSettingsOut | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    platformGetBrandingSettings()
      .then((res) => {
        setSettings(res.data)
        setError(null)
      })
      .catch((err) => setError(err.message || '加载失败'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const previewUrl = resolvePlatformBrandingAssetUrl(settings?.favicon?.url) || BUNDLED_FAVICON_32
  const previewLabel = settings?.favicon?.original_filename || 'Vistoera 默认 favicon'

  const uploadProps: UploadProps = {
    accept: 'image/png,image/webp',
    showUploadList: false,
    maxCount: 1,
    beforeUpload: (file) => {
      const okType = file.type === 'image/png' || file.type === 'image/webp'
      if (!okType) {
        message.error('仅支持 PNG 或 WebP')
        return Upload.LIST_IGNORE
      }
      if (file.size > 512 * 1024) {
        message.error('文件不能超过 512 KB')
        return Upload.LIST_IGNORE
      }
      setUploading(true)
      platformUploadBrandingFavicon(file)
        .then((res) => {
          setSettings(res.data)
          if (res.data.favicon?.url) {
            applyUploadedPlatformFavicon(res.data.favicon.url, res.data.favicon.content_type)
          }
          message.success('浏览器标签图标已更新')
        })
        .catch((err) => message.error(err.message || '上传失败'))
        .finally(() => setUploading(false))
      return Upload.LIST_IGNORE
    },
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ fontSize: 20, lineHeight: '26px', fontWeight: 600, color: platformColors.primaryText, marginBottom: 4 }}>
        系统设置
      </div>
      <Typography.Paragraph style={{ color: platformColors.secondaryText, marginBottom: 24 }}>
        平台级系统配置。当前仅开放品牌设置；更多选项将在后续版本接入。
      </Typography.Paragraph>

      {error && (
        <Typography.Text type="danger" style={{ display: 'block', marginBottom: 16 }}>
          {error}
        </Typography.Text>
      )}

      <SectionCard title="品牌设置">
        <Spin spinning={loading}>
        <div style={{ marginBottom: 8, fontWeight: 600, color: platformColors.primaryText }}>浏览器标签图标</div>
        <div
          style={{
            display: 'flex',
            gap: 20,
            alignItems: 'center',
            marginBottom: 16,
            padding: 16,
            borderRadius: 8,
            border: `1px solid ${platformColors.border}`,
            background: platformColors.surface,
          }}
        >
          <Image
            src={previewUrl}
            alt="当前 favicon"
            width={64}
            height={64}
            preview={false}
            style={{ borderRadius: 8, background: '#050d1f' }}
          />
          <div>
            <div style={{ fontSize: 13, color: platformColors.primaryText }}>当前：{previewLabel}</div>
            {settings?.updated_at && (
              <div style={{ fontSize: 12, color: platformColors.mutedText, marginTop: 4 }}>
                更新于 {formatDateTime(settings.updated_at)}
              </div>
            )}
          </div>
        </div>
        <Typography.Paragraph style={{ fontSize: 13, color: platformColors.secondaryText }}>
          Vistoera 浏览器标签页中显示的小图标。建议上传正方形 PNG / WebP（至少 32×32）。
        </Typography.Paragraph>
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />} loading={uploading} type="primary">
            上传新图标
          </Button>
        </Upload>
        </Spin>
      </SectionCard>
    </div>
  )
}
